import type { QualificationForm } from "../types";

// Mock data for useForms
export const mockForms: QualificationForm[] = [
  {
    id: "form_1",
    client_id: "demo_client",
    name: "Job Application Qualification",
    description: "Standard qualification form for job applicants",
    status: "active",
    questions: {
      questions: [
        {
          id: "q1",
          type: "boolean",
          label: "Do you have relevant experience?",
          description: "Have you worked in a similar role before?",
          required: true,
          scoring_mode: "weight",
          scoring: {
            values: { yes: 20, no: 0 },
            default: 0,
            weight: 1.5,
          },
        },
        {
          id: "q2",
          type: "number",
          label: "Years of experience",
          description: "Total years of relevant professional experience",
          required: true,
          min: 0,
          max: 50,
          scoring_mode: "weight",
          scoring: {
            ranges: [
              { operator: "gte", threshold: 5, value: 20 },
              { operator: "gte", threshold: 2, value: 15 },
              { operator: "gte", threshold: 0, value: 10 },
            ],
            default: 0,
            weight: 1.2,
          },
        },
        {
          id: "q3",
          type: "select",
          label: "What is your primary role?",
          required: true,
          options: ["Developer", "Designer", "Manager", "Analyst", "Other"],
          scoring_mode: "weight",
          scoring: {
            values: {
              Developer: 15,
              Designer: 12,
              Manager: 10,
              Analyst: 10,
              Other: 5,
            },
            default: 5,
            weight: 1.0,
          },
        },
        {
          id: "q4",
          type: "text",
          label: "Why are you interested in this position?",
          description: "Please provide a brief explanation",
          required: true,
          scoring_mode: "weight",
          scoring: {
            default: 10,
            weight: 0.8,
            scoring_range: { min: 0, max: 25 },
            scoring_logic: "Rate based on clarity, enthusiasm, and relevance",
          },
        },
        {
          id: "q5",
          type: "select",
          label: "When can you start?",
          required: true,
          options: ["Immediate", "2 weeks", "1 month", "2+ months"],
          scoring_mode: "weight",
          scoring: {
            values: {
              Immediate: 20,
              "2 weeks": 15,
              "1 month": 10,
              "2+ months": 5,
            },
            default: 0,
            weight: 0.8,
          },
        },
      ],
    },
    auto_accept_threshold: 75,
    auto_reject_threshold: 40,
    scoring_strategy: "weighted",
    created_at: Date.now() - 90 * 24 * 60 * 60 * 1000,
    updated_at: Date.now() - 30 * 24 * 60 * 60 * 1000,
    created_by: "admin_user",
    updated_by: "admin_user",
    is_deleted: false,
  },
  {
    id: "form_2",
    client_id: "demo_client",
    name: "Service Provider Screening",
    description: "Qualification form for service provider partners",
    status: "active",
    questions: {
      questions: [
        {
          id: "q1",
          type: "boolean",
          label: "Are you licensed and insured?",
          required: true,
          scoring_mode: "minimum",
          scoring: {
            values: { yes: 100, no: 0 },
            default: 0,
          },
        },
        {
          id: "q2",
          type: "number",
          label: "Years in business",
          required: true,
          min: 0,
          max: 100,
          scoring_mode: "weight",
          scoring: {
            ranges: [
              { operator: "gte", threshold: 10, value: 25 },
              { operator: "gte", threshold: 5, value: 20 },
              { operator: "gte", threshold: 2, value: 15 },
              { operator: "gte", threshold: 0, value: 10 },
            ],
            default: 0,
            weight: 1.0,
          },
        },
        {
          id: "q3",
          type: "select",
          label: "Service coverage area",
          required: true,
          options: ["Local", "Regional", "National", "International"],
          scoring_mode: "weight",
          scoring: {
            values: {
              International: 20,
              National: 15,
              Regional: 10,
              Local: 5,
            },
            default: 0,
            weight: 0.9,
          },
        },
        {
          id: "q4",
          type: "number",
          label: "Number of employees",
          required: true,
          min: 1,
          max: 10000,
          scoring_mode: "weight",
          scoring: {
            ranges: [
              { operator: "gte", threshold: 50, value: 15 },
              { operator: "gte", threshold: 10, value: 12 },
              { operator: "gte", threshold: 1, value: 8 },
            ],
            default: 0,
            weight: 0.8,
          },
        },
        {
          id: "q5",
          type: "text",
          label: "Describe your quality assurance process",
          required: true,
          scoring_mode: "weight",
          scoring: {
            default: 10,
            weight: 1.2,
            scoring_range: { min: 0, max: 30 },
            scoring_logic:
              "Evaluate based on comprehensiveness and specificity",
          },
        },
      ],
    },
    auto_accept_threshold: 80,
    auto_reject_threshold: 50,
    scoring_strategy: "mixed",
    created_at: Date.now() - 60 * 24 * 60 * 60 * 1000,
    updated_at: Date.now() - 15 * 24 * 60 * 60 * 1000,
    created_by: "admin_user",
    updated_by: "admin_user",
    is_deleted: false,
  },
  {
    id: "form_3",
    client_id: "demo_client",
    name: "Customer Onboarding Survey",
    description: "Initial qualification survey for new customers",
    status: "active",
    questions: {
      questions: [
        {
          id: "q1",
          type: "select",
          label: "How did you hear about us?",
          required: true,
          options: [
            "Search Engine",
            "Social Media",
            "Referral",
            "Advertisement",
            "Other",
          ],
          scoring_mode: "weight",
          scoring: {
            values: {
              Referral: 20,
              "Social Media": 15,
              "Search Engine": 12,
              Advertisement: 10,
              Other: 5,
            },
            default: 0,
            weight: 0.7,
          },
        },
        {
          id: "q2",
          type: "select",
          label: "What is your primary interest?",
          required: true,
          options: [
            "Product Purchase",
            "Service Inquiry",
            "Partnership",
            "General Information",
          ],
          scoring_mode: "weight",
          scoring: {
            values: {
              "Product Purchase": 25,
              "Service Inquiry": 20,
              Partnership: 15,
              "General Information": 5,
            },
            default: 0,
            weight: 1.5,
          },
        },
        {
          id: "q3",
          type: "number",
          label: "Estimated budget (in thousands)",
          required: false,
          min: 0,
          max: 1000,
          scoring_mode: "weight",
          scoring: {
            ranges: [
              { operator: "gte", threshold: 50, value: 25 },
              { operator: "gte", threshold: 20, value: 20 },
              { operator: "gte", threshold: 10, value: 15 },
              { operator: "gte", threshold: 0, value: 10 },
            ],
            default: "ignore",
            weight: 1.3,
          },
        },
        {
          id: "q4",
          type: "select",
          label: "Decision timeline",
          required: true,
          options: [
            "Immediate",
            "Within 1 month",
            "1-3 months",
            "3+ months",
            "Just researching",
          ],
          scoring_mode: "weight",
          scoring: {
            values: {
              Immediate: 25,
              "Within 1 month": 20,
              "1-3 months": 12,
              "3+ months": 8,
              "Just researching": 3,
            },
            default: 0,
            weight: 1.1,
          },
        },
      ],
    },
    auto_accept_threshold: 70,
    auto_reject_threshold: 30,
    scoring_strategy: "weighted",
    created_at: Date.now() - 45 * 24 * 60 * 60 * 1000,
    updated_at: Date.now() - 10 * 24 * 60 * 60 * 1000,
    created_by: "admin_user",
    updated_by: "admin_user",
    is_deleted: false,
  },
];
